/**
 * Independent Part A / Part B duration helpers (hybrid interviews).
 *
 * Storage model (Option B):
 * - Part A budget → MCQ `total_time_seconds` (minutes in admin UI convert to seconds).
 * - Part B budget → `interviews.part_b_duration_minutes`.
 * - `estimated_duration_minutes` is the informational total (Part A + Part B in hybrid)
 *   for calendar invites, overview, and list cards.
 *
 * Do not use proctoring `part_a_question_seconds` / `part_b_turn_seconds` as part budgets.
 * Legacy rows with NULL `part_b_duration_minutes` treat `estimated_duration_minutes`
 * as the oral (Part B) budget — the pre-split VoiceInterviewSession behavior.
 */

export type PartTimerMode = 'hybrid' | 'part_a' | 'part_b';

export const PART_A_DURATION_MINUTES_MIN = 1;
export const PART_A_DURATION_MINUTES_MAX = 180;
export const PART_B_DURATION_MINUTES_MIN = 5;
export const PART_B_DURATION_MINUTES_MAX = 180;
export const TOTAL_DURATION_MINUTES_MAX = 360;
export const DEFAULT_PART_A_MINUTES = 15;
export const DEFAULT_PART_B_MINUTES = 20;
export const DEFAULT_ESTIMATED_MINUTES = 30;

export type InterviewDurationFields = {
  estimated_duration_minutes?: number | null;
  part_b_duration_minutes?: number | null;
};

export type SessionTimingSnapshot = {version:number;part_b_duration_seconds:number};
export class SessionTimingDefinitionError extends Error {}

// Only apply this to the owning session's interview. Unknown/corrupt versions
// must not silently replace an accepted budget with today's mutable settings.
export function applySessionOralTiming<T extends InterviewDurationFields>(interview:T,snapshot?:SessionTimingSnapshot|null):T {
  if(snapshot==null)return interview;
  if(snapshot.version!==1 || !Number.isInteger(snapshot.part_b_duration_seconds)
    || snapshot.part_b_duration_seconds<300 || snapshot.part_b_duration_seconds>10800
    || snapshot.part_b_duration_seconds%60!==0)throw new SessionTimingDefinitionError('Session timing definition is invalid or unsupported.');
  // The AI interview normalizer consumes duration_minutes; the calendar/list
  // total remains estimated_duration_minutes and must not become the oral cap.
  return {...interview,part_b_duration_minutes:snapshot.part_b_duration_seconds/60,
    duration_minutes:snapshot.part_b_duration_seconds/60};
}

export type McqTimerSettingsLike = {
  total_time_seconds?: number | null;
  time_per_question_seconds?: number | null;
  question_count?: number | null;
};

function clampInt(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.round(value)));
}

export function clampPartAMinutes(value: number): number {
  return clampInt(value, PART_A_DURATION_MINUTES_MIN, PART_A_DURATION_MINUTES_MAX);
}

export function clampPartBMinutes(value: number): number {
  return clampInt(value, PART_B_DURATION_MINUTES_MIN, PART_B_DURATION_MINUTES_MAX);
}

export function partASecondsFromMinutes(minutes: number): number {
  return clampPartAMinutes(minutes) * 60;
}

export function partAMinutesFromMcqSettings(settings?: McqTimerSettingsLike | null): number {
  const totalSeconds = Number(settings?.total_time_seconds);
  if (Number.isFinite(totalSeconds) && totalSeconds >= 60) {
    return clampPartAMinutes(Math.round(totalSeconds / 60));
  }
  const perQuestion = Number(settings?.time_per_question_seconds);
  const questionCount = Number(settings?.question_count);
  if (Number.isFinite(perQuestion) && perQuestion > 0 && Number.isFinite(questionCount) && questionCount > 0) {
    return clampPartAMinutes(Math.max(1, Math.round((perQuestion * questionCount) / 60)));
  }
  return DEFAULT_PART_A_MINUTES;
}

export function resolvePartBDurationMinutes(interview?: InterviewDurationFields | null): number {
  const dedicated = Number(interview?.part_b_duration_minutes);
  if (Number.isFinite(dedicated) && dedicated > 0) {
    return clampPartBMinutes(dedicated);
  }
  const estimated = Number(interview?.estimated_duration_minutes);
  if (Number.isFinite(estimated) && estimated > 0) {
    return clampPartBMinutes(estimated);
  }
  return DEFAULT_PART_B_MINUTES;
}

export function resolvePartBDurationSeconds(interview?: InterviewDurationFields | null): number {
  return Math.max(60, resolvePartBDurationMinutes(interview) * 60);
}

export function resolveTotalDurationMinutes(input: {
  mode: PartTimerMode;
  partAMinutes?: number | null;
  partBMinutes?: number | null;
}): number {
  const partA = clampPartAMinutes(Number(input.partAMinutes) || DEFAULT_PART_A_MINUTES);
  const partB = clampPartBMinutes(Number(input.partBMinutes) || DEFAULT_PART_B_MINUTES);
  if (input.mode === 'part_a') return partA;
  if (input.mode === 'part_b') return partB;
  return Math.min(TOTAL_DURATION_MINUTES_MAX, partA + partB);
}

export function buildInterviewDurationPayload(input: {
  mode: PartTimerMode;
  partAMinutes?: number | null;
  partBMinutes?: number | null;
}): {
  estimated_duration_minutes: number;
  part_b_duration_minutes: number | null;
} {
  const partA = clampPartAMinutes(Number(input.partAMinutes) || DEFAULT_PART_A_MINUTES);
  const partB = clampPartBMinutes(Number(input.partBMinutes) || DEFAULT_PART_B_MINUTES);
  if (input.mode === 'part_a') {
    return { estimated_duration_minutes: partA, part_b_duration_minutes: null };
  }
  if (input.mode === 'part_b') {
    return { estimated_duration_minutes: partB, part_b_duration_minutes: partB };
  }
  return {
    estimated_duration_minutes: Math.min(TOTAL_DURATION_MINUTES_MAX, partA + partB),
    part_b_duration_minutes: partB
  };
}

/**
 * List/assign labels from stored interview columns (no MCQ fetch).
 * After Option B saves: estimated = total, part_b = oral budget, so
 * Part A minutes ≈ estimated − part_b when both are present.
 */
export function formatPartDurationLabel(
  interview?: InterviewDurationFields | null,
  mode?: PartTimerMode | null
): string {
  const total = Number(interview?.estimated_duration_minutes) || DEFAULT_ESTIMATED_MINUTES;
  const partBRaw = interview?.part_b_duration_minutes;
  const hasPartB = partBRaw != null && Number.isFinite(Number(partBRaw)) && Number(partBRaw) > 0;
  const partB = hasPartB ? clampPartBMinutes(Number(partBRaw)) : null;

  if (mode === 'part_a') return `A ${total}m`;
  if (mode === 'part_b') return `B ${partB ?? total}m`;

  if (partB != null) {
    const partA = total - partB;
    if (partA > 0) return `A ${partA}m + B ${partB}m`;
    return `B ${partB}m`;
  }
  return `${total}m`;
}

export function formatCandidateDurationCopy(input: {
  mode: PartTimerMode;
  partAMinutes?: number | null;
  partBMinutes?: number | null;
  estimatedMinutes?: number | null;
}): string {
  if (input.mode === 'hybrid') {
    const partA = input.partAMinutes ?? DEFAULT_PART_A_MINUTES;
    const partB = input.partBMinutes
      ?? resolvePartBDurationMinutes({
        estimated_duration_minutes: input.estimatedMinutes,
        part_b_duration_minutes: input.partBMinutes
      });
    return `Part A: ${partA} min · Part B: ${partB} min`;
  }
  if (input.mode === 'part_a') {
    return `Part A: ${input.partAMinutes ?? DEFAULT_PART_A_MINUTES} min`;
  }
  return `Part B: ${
    input.partBMinutes
    ?? resolvePartBDurationMinutes({ estimated_duration_minutes: input.estimatedMinutes })
  } min`;
}
